import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    StyleSheet,
    View,
    SafeAreaView,
    useWindowDimensions,
    FlatList,
    ActivityIndicator,
    TouchableOpacity,
} from "react-native";
import { Card, Checkbox } from "react-native-paper";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import { useTranslation } from "react-i18next";
import { useHRAdminEmployees } from "../../../hooks/useHRAdminEmployees";
import { formatDateForUI } from "../../../constants/FormatDate";
import CustomButton from "../../../components/CustomButton";

const InventoryListView = React.memo(
    ({
        item,
        styles,
        expanded,
        onToggle,
        t,
    }) => {
        return (
            <SafeAreaView>
                <TouchableOpacity onPress={onToggle} activeOpacity={0.8}>
                    <SafeAreaView style={styles.row}>
                        <View style={styles.smallItem}>
                            <CustomLabel value={item?.serno ?? ''} type="REGULAR" />
                        </View>

                        <View style={styles.item}>
                            <CustomLabel value={item?.type ?? ''} type="REGULAR" />
                        </View>

                        <View style={styles.item}>
                            <CustomLabel value={item?.model ?? ''} type="REGULAR" />
                        </View>

                        <View style={styles.item}>
                            <CustomLabel value={item?.category ?? ''} type="REGULAR" />
                        </View>

                        <View style={styles.item}>
                            <CustomLabel value={formatDateForUI(item?.dol) ?? ''} type="REGULAR" />
                        </View>

                        <View style={styles.item}>
                            <CustomLabel value={formatDateForUI(item?.dor) ?? ''} type="REGULAR" />
                        </View>

                        <View style={styles.returnedItem}>
                            <Checkbox
                                status={item?.Returned === 'YES' ? 'checked' : 'unchecked'}
                                disabled={true}
                                color={item?.Returned === 'YES' ? '#86BC25' : undefined}
                                style={styles.checkbox}
                            />
                        </View>

                        <View style={styles.smallItem}>
                            <CustomLabel value={item?.userid ?? ''} type="REGULAR" />
                        </View>

                        <View style={styles.item}>
                            <CustomLabel value={formatDateForUI(item?.doc) ?? ''} type="REGULAR" />
                        </View>

                        {/*<CustomButton
                            onPress={() => {/* TODO: open item details from inventory / }}
                            iconName='open-outline'
                            show='HOME_BELL'
                            type='HOME_BELL'
                            textTop='0'
                            sz={30}
                        />*/}
                    </SafeAreaView>
                </TouchableOpacity>

                {expanded && (
                    <SafeAreaView style={styles.commentRow}>
                        <View style={styles.commentLabelWrap}>
                            <CustomLabel value={"Comment:"} type="SUBTITLE" />
                        </View>
                        <View style={styles.commentValueWrap}>
                            <CustomLabel value={item?.comment ?? '-'} type="REGULAR" />
                        </View>
                    </SafeAreaView>
                )}
            </SafeAreaView>
        );
    }
);

const HRAdminEmplInventory = (props) => {
    const { width } = useWindowDimensions();
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width);
    const { t } = useTranslation();
    const ref = useRef(null);

    const [isLoading, setIsLoading] = useState(false);
    const [invData, setInvData] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});

    const { getEmplInventory } = useHRAdminEmployees();

    useEffect(() => {
        loadData();
    }, [props?.selectedEmployee, props?.selectedCompany]);

    const loadData = async () => {
        try {
            setIsLoading(true);

            getEmplInventory(props?.selectedEmployee?.employeeID)
                .then((res) => {
                    setInvData(res || []);
                })
                .catch((err) => {
                    console.log(err);
                });
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleExpanded = (key) => {
        setExpandedRows((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const renderInventory = useCallback(
        ({ item, index }) => {
            const rowKey = item?.serno ? `${item.serno}-${index}` : `${index}`;
            return (
                <Card style={[common.cardstyle]}>
                    <Card.Content>
                        <InventoryListView
                            item={item}
                            index={index}
                            styles={styles}
                            t={t}
                            expanded={!!expandedRows[rowKey]}
                            onToggle={() => toggleExpanded(rowKey)}
                            colors={colors}
                        />
                    </Card.Content>
                </Card>
            );
        },
        [styles, expandedRows, colors]
    );

    if (isLoading) {
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        );
    }

    return (
        <SafeAreaView style={styles.mainView}>
            <FlatList
                data={invData}
                keyExtractor={(item, index) => `${item?.serno ?? 'inv'}-${index}`}
                renderItem={renderInventory}
                refreshing={isLoading}
                ref={ref}
                ListHeaderComponent={
                    <SafeAreaView style={common.listTitle}>
                        <SafeAreaView style={styles.row}>
                            <View style={styles.smallItem}>
                                <CustomLabel value={"Serial No"} type="SUBTITLE" />
                            </View>
                            <View style={styles.item}>
                                <CustomLabel value={"Type"} type="SUBTITLE" />
                            </View>
                            <View style={styles.item}>
                                <CustomLabel value={"Model"} type="SUBTITLE" />
                            </View>
                            <View style={styles.item}>
                                <CustomLabel value={"Category"} type="SUBTITLE" />
                            </View>
                            <View style={styles.item}>
                                <CustomLabel value={"DOL"} type="SUBTITLE" />
                            </View>
                            <View style={styles.item}>
                                <CustomLabel value={"DOR"} type="SUBTITLE" />
                            </View>
                            <View style={styles.returnedItem}>
                                <CustomLabel value={"Returned"} type="SUBTITLE" />
                            </View>
                            <View style={styles.smallItem}>
                                <CustomLabel value={"User ID"} type="SUBTITLE" />
                            </View>
                            <View style={styles.item}>
                                <CustomLabel value={"Doc"} type="SUBTITLE" />
                            </View>
                            <View style={styles.arrowItem} />
                        </SafeAreaView>
                        <SafeAreaView style={common.divider} />
                    </SafeAreaView>
                }
                ListEmptyComponent={
                    isLoading ? (
                        <ActivityIndicator animating={true} color={colors.blue} />
                    ) : (
                        <CustomLabel
                            value={t("common:noDataAvailable")}
                            type="NO_FUNCTIONS"
                        />
                    )
                }
                initialNumToRender={4}
            />
        </SafeAreaView>
    );
};

const makeStyles = (colors, width) =>
    StyleSheet.create({
        mainView: {
            flex: 1,
        },
        row: {
            flex: 1,
            flexDirection: "row",
            alignItems: "center",
        },
        item: {
            flex: 1.2,
            justifyContent: "center",
            marginRight: 6,
            alignItems: "center",
        },
        smallItem: {
            flex: 0.9,
            justifyContent: "center",
            marginRight: 6,
            alignItems: "center",
        },
        returnedItem: {
            flex: 0.8,
            justifyContent: "center",
            marginRight: 6,
            alignItems: "center",
        },
        arrowItem: {
            flex: 0.4,
            justifyContent: "center",
            alignItems: "center",
        },
        checkbox: {
            marginBottom: 6,
        },
        commentRow: {
            marginTop: 10,
            paddingTop: 10,
            borderTopWidth: 1,
            borderTopColor: colors.card_line || "#ddd",
        },
        commentLabelWrap: {
            marginBottom: 6,
        },
        commentValueWrap: {
            width: "100%",
            alignItems:'center'
        },
        loader: {
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "4%",
        },
    });

export default HRAdminEmplInventory;